import { useTranslation } from 'react-i18next'
import { NavLink } from 'react-router-dom'
import { PrivacyDataPanel } from '@/components/compliance/PrivacyDataPanel'
import { NotificationEventsPanel } from '@/components/settings/notifications/NotificationEventsPanel'
import { cn } from '@/lib/utils'

export type MyAccountSection = 'privacy' | 'notifications'

interface MyAccountPageProps {
  section: MyAccountSection
}

const MY_ACCOUNT_TABS: Array<{
  section: MyAccountSection
  to: string
  labelKey: 'myAccount.tabs.privacy' | 'myAccount.tabs.notifications'
}> = [
  {
    section: 'privacy',
    to: '/privacy-data',
    labelKey: 'myAccount.tabs.privacy',
  },
  {
    section: 'notifications',
    to: '/notifications/preferences',
    labelKey: 'myAccount.tabs.notifications',
  },
]

export default function MyAccountPage({ section }: MyAccountPageProps) {
  const { t } = useTranslation('compliance')
  const activeTab = MY_ACCOUNT_TABS.find(tab => tab.section === section)
  const subtabLabel =
    section === 'notifications'
      ? t('myAccount.subtabs.notificationPreferences')
      : t('myAccount.subtabs.privacy')

  return (
    <div className='space-y-5'>
      <div className='overflow-hidden rounded-2xl border border-[#e9edf5] bg-white shadow-[0_12px_28px_-10px_rgba(38,45,66,0.14),0_4px_10px_-6px_rgba(38,45,66,0.08)]'>
        <nav className='overflow-x-auto border-b border-[#eef2f6] px-3 pb-2 pt-2'>
          <div className='flex min-w-max items-center gap-2'>
            {MY_ACCOUNT_TABS.map(tab => (
              <NavLink
                key={tab.section}
                to={tab.to}
                className={cn(
                  'rounded-xl px-3.5 py-2 text-[16px] font-medium leading-[22px] transition-colors',
                  tab.section === section
                    ? 'bg-[#FF5A1F] text-white shadow-sm'
                    : 'text-[#3D434E] hover:bg-[#fff3ed]'
                )}
              >
                {t(tab.labelKey)}
              </NavLink>
            ))}
          </div>
        </nav>

        {activeTab ? (
          <nav className='overflow-x-auto border-b border-[#eef2f6] px-3'>
            <div className='flex min-w-max items-center gap-1'>
              <NavLink
                to={activeTab.to}
                className='relative whitespace-nowrap px-3 py-3 text-[16px] font-medium leading-[22px] text-[#3D434E]'
              >
                {subtabLabel}
                <span
                  className='absolute inset-x-3 bottom-0 h-0.5 rounded-full bg-[#FF5A1F]'
                  aria-hidden='true'
                />
              </NavLink>
            </div>
          </nav>
        ) : null}
      </div>

      <div className='min-w-0'>
        {section === 'privacy' ? (
          <PrivacyDataPanel />
        ) : (
          <NotificationEventsPanel mode='user' />
        )}
      </div>
    </div>
  )
}
